"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.greatCircleArc = greatCircleArc;
exports.meridian = meridian;
exports.parallel = parallel;

var _proj = require("../../proj.js");

var _math = require("../../math.js");

/**
 * @module ol/geom/flat/geodesic
 */

/**
 * @param {function(number): import("../../coordinate.js").Coordinate} interpolate Interpolate function.
 * @param {import("../../proj.js").TransformFunction} transform Transform from longitude/latitude to
 *     projected coordinates.
 * @param {number} squaredTolerance Squared tolerance.
 * @return {Array<number>} Flat coordinates.
 */
function line(interpolate, transform, squaredTolerance) {
  // FIXME reduce garbage generation
  // FIXME optimize stack operations

  /** @type {Array<number>} */
  var flatCoordinates = [];
  var geoA = interpolate(0);
  var geoB = interpolate(1);
  var a = transform(geoA);
  var b = transform(geoB);
  /** @type {Array<import("../../coordinate.js").Coordinate>} */

  var geoStack = [geoB, geoA];
  /** @type {Array<import("../../coordinate.js").Coordinate>} */

  var stack = [b, a];
  /** @type {Array<number>} */

  var fractionStack = [1, 0];
  /** @type {!Object<string, boolean>} */

  var fractions = {};
  var maxIterations = 1e5;
  var geoM, m, fracA, fracB, fracM, key;

  while (--maxIterations > 0 && fractionStack.length > 0) {
    // Pop the a coordinate off the stack
    fracA = fractionStack.pop();
    geoA = geoStack.pop();
    a = stack.pop(); // Add the a coordinate if it has not been added yet

    key = fracA.toString();

    if (!(key in fractions)) {
      flatCoordinates.push(a[0], a[1]);
      fractions[key] = true;
    } // Pop the b coordinate off the stack


    fracB = fractionStack.pop();
    geoB = geoStack.pop();
    b = stack.pop(); // Find the m point between the a and b coordinates

    fracM = (fracA + fracB) / 2;
    geoM = interpolate(fracM);
    m = transform(geoM);

    if ((0, _math.squaredSegmentDistance)(m[0], m[1], a[0], a[1], b[0], b[1]) < squaredTolerance) {
      // If the m point is sufficiently close to the straight line, then we
      // discard it.  Just use the b coordinate and move on to the next line
      // segment.
      flatCoordinates.push(b[0], b[1]);
      key = fracB.toString();
      fractions[key] = true;
    } else {
      // Otherwise, we need to subdivide the current line segment.  Split it
      // into two and push the two line segments onto the stack.
      fractionStack.push(fracB, fracM, fracM, fracA);
      stack.push(b, m, m, a);
      geoStack.push(geoB, geoM, geoM, geoA);
    }
  }

  return flatCoordinates;
}
/**
 * Generate a great-circle arcs between two lat/lon points.
 * @param {number} lon1 Longitude 1 in degrees.
 * @param {number} lat1 Latitude 1 in degrees.
 * @param {number} lon2 Longitude 2 in degrees.
 * @param {number} lat2 Latitude 2 in degrees.
 * @param {import("../../proj/Projection.js").default} projection Projection.
 * @param {number} squaredTolerance Squared tolerance.
 * @return {Array<number>} Flat coordinates.
 */


function greatCircleArc(lon1, lat1, lon2, lat2, projection, squaredTolerance) {
  var geoProjection = (0, _proj.get)('EPSG:4326');
  var cosLat1 = Math.cos((0, _math.toRadians)(lat1));
  var sinLat1 = Math.sin((0, _math.toRadians)(lat1));
  var cosLat2 = Math.cos((0, _math.toRadians)(lat2));
  var sinLat2 = Math.sin((0, _math.toRadians)(lat2));
  var cosDeltaLon = Math.cos((0, _math.toRadians)(lon2 - lon1));
  var sinDeltaLon = Math.sin((0, _math.toRadians)(lon2 - lon1));
  var d = sinLat1 * sinLat2 + cosLat1 * cosLat2 * cosDeltaLon;
  return line(
  /**
   * @param {number} frac Fraction.
   * @return {import("../../coordinate.js").Coordinate} Coordinate.
   */
  function (frac) {
    if (1 <= d) {
      return [lon2, lat2];
    }

    var D = frac * Math.acos(d);
    var cosD = Math.cos(D);
    var sinD = Math.sin(D);
    var y = sinDeltaLon * cosLat2;
    var x = cosLat1 * sinLat2 - sinLat1 * cosLat2 * cosDeltaLon;
    var theta = Math.atan2(y, x);
    var lat = Math.asin(sinLat1 * cosD + cosLat1 * sinD * Math.cos(theta));
    var lon = (0, _math.toRadians)(lon1) + Math.atan2(Math.sin(theta) * sinD * cosLat1, cosD - sinLat1 * Math.sin(lat));
    return [(0, _math.toDegrees)(lon), (0, _math.toDegrees)(lat)];
  }, (0, _proj.getTransform)(geoProjection, projection), squaredTolerance);
}
/**
 * Generate a meridian (line at constant longitude).
 * @param {number} lon Longitude.
 * @param {number} lat1 Latitude 1.
 * @param {number} lat2 Latitude 2.
 * @param {import("../../proj/Projection.js").default} projection Projection.
 * @param {number} squaredTolerance Squared tolerance.
 * @return {Array<number>} Flat coordinates.
 */


function meridian(lon, lat1, lat2, projection, squaredTolerance) {
  var epsg4326Projection = (0, _proj.get)('EPSG:4326');
  return line(
  /**
   * @param {number} frac Fraction.
   * @return {import("../../coordinate.js").Coordinate} Coordinate.
   */
  function (frac) {
    return [lon, lat1 + (lat2 - lat1) * frac];
  }, (0, _proj.getTransform)(epsg4326Projection, projection), squaredTolerance);
}
/**
 * Generate a parallel (line at constant latitude).
 * @param {number} lat Latitude.
 * @param {number} lon1 Longitude 1.
 * @param {number} lon2 Longitude 2.
 * @param {import("../../proj/Projection.js").default} projection Projection.
 * @param {number} squaredTolerance Squared tolerance.
 * @return {Array<number>} Flat coordinates.
 */


function parallel(lat, lon1, lon2, projection, squaredTolerance) {
  var epsg4326Projection = (0, _proj.get)('EPSG:4326');
  return line(
  /**
   * @param {number} frac Fraction.
   * @return {import("../../coordinate.js").Coordinate} Coordinate.
   */
  function (frac) {
    return [lon1 + (lon2 - lon1) * frac, lat];
  }, (0, _proj.getTransform)(epsg4326Projection, projection), squaredTolerance);
}
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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